import { Alert, Autocomplete, Divider, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EnumerationMultiSelect, ToggleButtonItem, WO2Checkbox, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DatePickerTypes } from 'src/common/components/DateRangePickerPreDefinedDates';
import { useDebounce } from 'src/common/hooks';
import { ReportingGenerationClientCsvModal } from 'src/features/reporting/components/generation/ReportingGenerationClientCsvModal';
import {
  ClientAccountTypeForSearch,
  ClientSearchResult,
  ClientStatusType,
  DateOperatorTypes,
  GetFilteredClientEntity,
  ScheduledEventInput,
  ServiceType,
} from 'src/features/reporting/store/types';
import { DateRange } from 'src/store';
import { Props } from '../../container';

interface ClientFilterStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
}

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Soa.displayName, value: ServiceType.Soa.name },
];

export const ClientFilterStep = (props: ClientFilterStepProps) => {
  const [searchText, setSearchText] = useState<Record<string, string>>({
    AfslLicensee: '',
    Practice: '',
    Adviser: '',
    Group: '',
    Client: '',
  });
  const [searchEntity, setSearchEntity] = useState<keyof typeof searchText | ''>('');

  const [uploadCsvOpened, setUploadCsvOpened] = useState<boolean>(false);
  const [dateOperatorType, setDateOperatorType] = useState<DateOperatorTypes>('GreaterThan');
  const [exitDate, setExitDate] = useState<DateRange>({
    dateFrom: '',
    dateTo: '',
    dateRangeType: DatePickerTypes.DateOnly,
  });
  const [inceptionDate, setInceptionDate] = useState<DateRange>({
    dateFrom: '',
    dateTo: '',
    dateRangeType: DatePickerTypes.DateOnly,
  });
  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedSearchEntity = useDebounce(searchEntity, 500);

  const afslCacheRef = useRef<Map<number, GetFilteredClientEntity>>(new Map());
  const practiceCacheRef = useRef<Map<number, GetFilteredClientEntity>>(new Map());
  const adviserCacheRef = useRef<Map<number, GetFilteredClientEntity>>(new Map());
  const groupCacheRef = useRef<Map<number, GetFilteredClientEntity>>(new Map());
  const clientCacheRef = useRef<Map<number, GetFilteredClientEntity>>(new Map());

  const handleClientFilterChange = <T,>(field: string, value: T) => {
    props.formikProps.setFieldValue(`clientSearchFilter.${field}`, value);
  };

  const closeCsvUpload = () => setUploadCsvOpened(false);

  const handleAppendClientsFromCsv = (clients: ClientSearchResult[], allowTerminatedClients: boolean) => {
    const clientIds = clients.map((client) => client.clientId);

    clients.forEach((client) => {
      clientCacheRef.current.set(client.clientId, {
        entityCoreId: client.clientId,
        name: client.clientName,
      } as GetFilteredClientEntity);
    });

    props.formikProps.setFieldValue('clientSearchFilter.clientIds', clientIds);
    props.formikProps.setFieldValue('allowTerminatedClients', allowTerminatedClients);
  };

  const uploadClientList = props.formikProps.values.clientSearchFilter?.clientIds ?? [];

  const resolveFromCache = (cache: Map<number, GetFilteredClientEntity>, entities: GetFilteredClientEntity[], ids: number[]) => {
    entities.forEach((e) => cache.set(e.entityCoreId, e));
    return ids.map((id) => cache.get(id)).filter((e): e is GetFilteredClientEntity => e !== undefined);
  };

  const selectedAfslsItems = useMemo(
    () => resolveFromCache(afslCacheRef.current, props.scheduler.afsls, props.formikProps.values.clientSearchFilter?.afslIds ?? []),
    [props.formikProps.values.clientSearchFilter?.afslIds, props.scheduler.afsls]
  );
  const selectedPracticeItems = useMemo(
    () => resolveFromCache(practiceCacheRef.current, props.scheduler.practices, props.formikProps.values.clientSearchFilter?.practiceIds ?? []),
    [props.formikProps.values.clientSearchFilter?.practiceIds, props.scheduler.practices]
  );
  const selectedAdvisers = useMemo(
    () => resolveFromCache(adviserCacheRef.current, props.scheduler.advisers, props.formikProps.values.clientSearchFilter?.adviserIds ?? []),
    [props.formikProps.values.clientSearchFilter?.adviserIds, props.scheduler.advisers]
  );
  const selectedGroups = useMemo(
    () => resolveFromCache(groupCacheRef.current, props.scheduler.groups, props.formikProps.values.clientSearchFilter?.groupIds ?? []),
    [props.formikProps.values.clientSearchFilter?.groupIds, props.scheduler.groups]
  );
  const selectedClients = useMemo(
    () => resolveFromCache(clientCacheRef.current, props.scheduler.clients, props.formikProps.values.clientSearchFilter?.clientIds ?? []),
    [props.formikProps.values.clientSearchFilter?.clientIds, props.scheduler.clients]
  );
  const selectedModel = useMemo(
    () => props.bulk.models.find((model) => model.modelId === props.formikProps.values.clientSearchFilter?.modelId) ?? null,
    [props.bulk.models, props.formikProps.values.clientSearchFilter?.modelId]
  );

  const updateSearch = (entity: keyof typeof searchText, value: string, reason: string) => {
    if (reason !== 'reset') {
      setSearchText((prev) => ({ ...prev, [entity]: value }));
      setSearchEntity(entity);
    }
  };

  useEffect(() => {
    const serviceType = props.formikProps.values.clientSearchFilter?.serviceType;
    const searchText = debouncedSearchText[debouncedSearchEntity];
    props.fetchFilteredReportClients({
      afslIds: props.formikProps.values.clientSearchFilter?.afslIds ?? [],
      practiceIds: props.formikProps.values.clientSearchFilter?.practiceIds ?? [],
      adviserIds: props.formikProps.values.clientSearchFilter?.adviserIds ?? [],
      accountTypes: props.formikProps.values.clientSearchFilter?.clientTypes ?? [],
      clientStatus: props.formikProps.values.clientSearchFilter?.statuses ?? [],
      serviceType: serviceType ? [serviceType] : [],
      modelId: props.formikProps.values.clientSearchFilter?.modelId,
      searchText: searchText,
      searchEntity: searchText ? debouncedSearchEntity : undefined,
      exitDate: exitDate.dateTo,
      inceptionDate: inceptionDate.dateTo,
      dateOperatorType: dateOperatorType,
    });
  }, [
    props.formikProps.values.clientSearchFilter?.afslIds,
    props.formikProps.values.clientSearchFilter?.practiceIds,
    props.formikProps.values.clientSearchFilter?.adviserIds,
    props.formikProps.values.clientSearchFilter?.clientTypes,
    props.formikProps.values.clientSearchFilter?.statuses,
    props.formikProps.values.clientSearchFilter?.serviceType,
    props.formikProps.values.clientSearchFilter?.modelId,
    debouncedSearchText,
    debouncedSearchEntity,
    exitDate,
    inceptionDate,
    dateOperatorType,
  ]);

  useEffect(() => {
    if (props.scheduler.savedParameter) {
      props.getReportParametersById({
        id: props.scheduler.savedParameter.id,
      });
    }
  }, [props.scheduler.savedParameter]);

  useEffect(() => {
    props.fetchModelList(props.formikProps.values.clientSearchFilter?.afslIds ?? []);
  }, [props.formikProps.values.clientSearchFilter?.afslIds]);

  useEffect(() => {
    const modelId = props.formikProps.values.clientSearchFilter?.modelId;
    if (modelId && props.bulk.models.length > 0 && !props.bulk.models.some((model) => model.modelId === modelId)) {
      handleClientFilterChange('modelId', undefined);
    }
  }, [props.bulk.models]);

  return (
    <Grid container spacing={2} direction="column">
      {props.scheduler.allowStaticClients && (
        <Grid item>
          <WO2Button onClick={() => setUploadCsvOpened(true)} variant="contained">
            Import Client List
          </WO2Button>
        </Grid>
      )}
      {props.scheduler.allowStaticClients && uploadClientList.length > 0 && (
        <Grid item>
          <Alert severity="info">
            <Typography>{uploadClientList.length} clients included</Typography>
          </Alert>
        </Grid>
      )}

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="afsl-multiselect"
            multiple
            fullWidth
            options={props.scheduler.afsls}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            disabled={selectedClients.length > 0 || selectedGroups.length > 0 || props.scheduler.allowStaticClients}
            value={selectedAfslsItems}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" value={searchText.AfslLicensee} fullWidth />}
            inputValue={searchText.AfslLicensee}
            onInputChange={(_e, value, reason) => updateSearch('AfslLicensee', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'afslIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, AfslLicensee: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="practice-multiselect"
            multiple
            fullWidth
            options={props.scheduler.practices}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedPracticeItems}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="Practice" value={searchText.Practice} fullWidth />}
            inputValue={searchText.Practice}
            onInputChange={(_e, value, reason) => updateSearch('Practice', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'practiceIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Practice: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="adviser-multiselect"
            multiple
            fullWidth
            options={props.scheduler.advisers}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedAdvisers}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" value={searchText.Adviser} fullWidth />}
            inputValue={searchText.Adviser}
            onInputChange={(_e, value, reason) => updateSearch('Adviser', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'adviserIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Adviser: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="group-multiselect"
            multiple
            fullWidth
            options={props.scheduler.groups}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedGroups}
            disabled={props.scheduler.allowStaticClients}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" value={searchText.Group} fullWidth />}
            inputValue={searchText.Group}
            onInputChange={(_e, value, reason) => updateSearch('Group', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'groupIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Group: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="client-multiselect"
            multiple
            fullWidth
            options={props.scheduler.clients}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedClients}
            disabled={props.scheduler.allowStaticClients}
            getOptionLabel={(option) => `${option.name ?? ''} (${option.entityCoreId ?? 0})`}
            renderInput={(params) => <TextField {...params} margin="normal" label="Client Search" value={searchText.Client} fullWidth />}
            inputValue={searchText.Client}
            onInputChange={(_e, value, reason) => updateSearch('Client', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'clientIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Client: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <EnumerationMultiSelect
            label="Client Type"
            type={ClientAccountTypeForSearch}
            value={props.formikProps.values.clientSearchFilter?.clientTypes ?? []}
            onChange={(values) => handleClientFilterChange('clientTypes', values)}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="model-select"
            fullWidth
            options={props.bulk.models}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            disabled={props.scheduler.allowStaticClients}
            value={selectedModel}
            getOptionLabel={(option) => (option.code ? `${option.name} (${option.code})` : option.name ?? '')}
            renderInput={(params) => <TextField {...params} margin="normal" label="Model" fullWidth />}
            onChange={(_e, value) => handleClientFilterChange('modelId', value?.modelId)}
            isOptionEqualToValue={(option, value) => option.modelId === value.modelId}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <EnumerationMultiSelect
            label="Statuses"
            type={ClientStatusType}
            hidden={[ClientStatusType.Terminated.name]}
            value={props.formikProps.values.clientSearchFilter?.statuses ?? []}
            onChange={(values) => handleClientFilterChange('statuses', values)}
          />
        </Grid>
        <Grid item xs={12} sm={6} mt={1}>
          <WO2ToggleButtonGroup
            fullWidth
            buttons={serviceTypeToggleButtons}
            value={props.formikProps.values.clientSearchFilter?.serviceType ?? ''}
            onChangeHandler={(value) => handleClientFilterChange('serviceType', value)}
          />
        </Grid>
      </Grid>

      <Grid item container alignItems="center" spacing={1}>
        <Grid item xs={12} md={4} pr="10px">
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Date Operator Type</InputLabel>
            <Select value={dateOperatorType} onChange={(e) => setDateOperatorType(e.target.value as DateOperatorTypes)} label="Date Operator Type">
              <MenuItem value="GreaterThan">Greater Than</MenuItem>
              <MenuItem value="LessThan">Less Than</MenuItem>
              <MenuItem value="EqualsTo">Equals To</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <DateRangePicker dateRange={exitDate} dateOnlyLabel="Exit Date" setDateRange={setExitDate} isDateOnly />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateRangePicker dateRange={inceptionDate} dateOnlyLabel="Inception Date" setDateRange={setInceptionDate} isDateOnly />
        </Grid>
      </Grid>

      <Grid item>
        <Divider />
      </Grid>

      <Grid item>
        <WO2Checkbox
          label="Allow static client uploading"
          onChangeHandler={(value) => {
            props.setAllowStaticClients(value);
            props.formikProps.setFieldValue('clientSearchFilter.clientIds', []);
          }}
          checked={props.scheduler.allowStaticClients}
        />
      </Grid>

      <ReportingGenerationClientCsvModal
        {...props}
        handleAppendClientsFromCsv={handleAppendClientsFromCsv}
        isOpen={uploadCsvOpened}
        handleClose={closeCsvUpload}
      />
    </Grid>
  );
};
